import {
    AfterContentInit,
    Component,
    ContentChildren,
    Input,
    OnChanges,
    OnInit,
    QueryList,
    SimpleChanges,
    Output,
    EventEmitter
} from "@angular/core";
import {TranslateService} from "@ngx-translate/core";
import {SampleBoardMaterialRecipeItem} from "@app/strain/sample-boards/models/sample-board-material-recipe-item.model";
import {FormArray, FormBuilder, FormGroup, Validators} from "@angular/forms";

/**
 * The sample board recipe component is used in order to leverage the represenation of a sample board recipe in the system.
 *
 * It now only renders a static recipe view with the provided materials list.
 */
@Component({
    selector: 'app-sample-board-material-scan-list',
    templateUrl: './sample-board-material-scan-list.component.html',
    styleUrls: ['./sample-board-material-scan-list.component.scss']
})
export class SampleBoardMaterialScanListComponent implements OnChanges, OnInit, AfterContentInit {
    readonly i18nKey = 'admin.strain.sample-boards.details.tabs.materials.recipe';

    @Input() headerTitle?: string;
    @Input() headerSubtitle?: string;
    @Input() headerDescription?: string;
    @Input() headerLeftIcon = 'fa-microchip';
    @Input() headerBackground: 'default' | 'green' | 'yellow' | 'red' | 'disabled' | 'disabled-done' | 'none' = 'default';
    @Input() headerText: 'default' | 'green-text' | 'yellow-text' | 'red-text' = 'default';
    @Input() headerPlaceholderTitle?: string;
    @Input() headerPlaceholderSubtitle?: string;
    @Input() items: SampleBoardRecipeSourceItem[] = [];
    @Input() isLoading = false;
    @Input() loadingLabel?: string;
    @Input() canEdit = false;
    @Input() editable = false;

    @ContentChildren('[recipeFooterActions]', {descendants: true}) footerActions: QueryList<any>;

    internalItems: SampleBoardMaterialRecipeItem[] = [];
    hasFooterActions = false;
    formArray: FormArray;
    private formGroupMap: Map<number, FormGroup> = new Map<number, FormGroup>();

    @Output() save = new EventEmitter<SampleBoardMaterialRecipeItem[]>();
    @Output() cancel = new EventEmitter<void>();

    constructor(private translate: TranslateService,
                private fb: FormBuilder) {
    }

    ngOnInit(): void {
        this.initializeItems();
    }

    ngAfterContentInit(): void {
        this.hasFooterActions = this.footerActions && this.footerActions.length > 0;
        this.footerActions?.changes?.subscribe((actions: QueryList<any>) => this.hasFooterActions = actions.length > 0);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['items']) {
            this.initializeItems();
        }

        if (changes['editable'] && !changes['editable'].firstChange) {
            this.editable = this.canEdit ? changes['editable'].currentValue : false;
        }

        if (changes['canEdit'] && changes['canEdit'].currentValue === false) {
            this.editable = false;
        }
    }

    get resolvedPlaceholderTitle(): string {
        return this.headerPlaceholderTitle || this.translate.instant(`${this.i18nKey}.header.placeholderTitle`);
    }

    get resolvedPlaceholderSubtitle(): string {
        return this.headerPlaceholderSubtitle || this.translate.instant(`${this.i18nKey}.header.placeholderSubtitle`);
    }

    get footerTitle(): string {
        return this.translate.instant(`${this.i18nKey}.footer.overview`);
    }

    get footerSubtitle(): string {
        const count = (this.internalItems || []).length;
        return this.translate.instant(`${this.i18nKey}.footer.materialLines`, {count});
    }

    get headerRightIcon(): string {
        if (!this.canEdit) {
            return null;
        }
        return this.editable ? 'fa-lock' : 'fa-pencil';
    }

    private initializeItems(): void {
        const base = (this.items && this.items.length ? this.items : [])
            .map((item, idx) => {
                const normalized = this.normalizeItem(item);
                if (normalized.id === null || normalized.id === undefined) {
                    // Use a negative identifier for the items that doesn't have ids
                    normalized.id = -(idx + 1);
                }
                return normalized;
            });

        this.internalItems = base.length ? base : [];
        const nextMap = new Map<number, FormGroup>();
        const groups = this.internalItems.map(item => {
            const existing = this.formGroupMap.get(item.id);
            const group = existing || this.buildGroup(item);
            nextMap.set(item.id, group);
            return group;
        });
        this.formGroupMap = nextMap;
        this.formArray = this.fb.array(groups);
    }

    private normalizeItem(item: SampleBoardRecipeSourceItem): SampleBoardMaterialRecipeItem {
        return item instanceof SampleBoardMaterialRecipeItem
            ? item
            : new SampleBoardMaterialRecipeItem(item);
    }

    private buildGroup(item: SampleBoardMaterialRecipeItem): FormGroup {
        const quantityValue = item.quantity != null ? item.quantity : 0;
        return this.fb.group({
            quantity: [quantityValue],
            code: [item.code || '', [Validators.required]]
        });
    }

    getOrCreateGroup(item: SampleBoardMaterialRecipeItem): FormGroup {
        if (!item) {
            return null;
        }
        const existing = this.formGroupMap.get(item.id);
        if (existing) {
            return existing;
        }
        const group = this.buildGroup(item);
        this.formGroupMap.set(item.id, group);
        return group;
    }

    onSave(): void {
        const groups = this.internalItems.map(item => this.getOrCreateGroup(item));
        const hasInvalid = groups.some(g => !g?.valid);
        if (hasInvalid) {
            groups.forEach(g => g?.markAllAsTouched());
            return;
        }
        const updated = this.internalItems.map((item, idx) => {
            const group = groups[idx];
            return new SampleBoardMaterialRecipeItem({
                ...item,
                quantity: group?.get('quantity')?.value ?? item.quantity ?? 0,
                code: group?.get('code')?.value
            });
        });
        this.save.emit(updated);
    }

    onCancel(): void {
        this.toggleEditable();
        this.cancel.emit();
    }

    toggleEditable(): void {
        if (!this.canEdit) {
            return;
        }
        this.editable = !this.editable;
    }
}

export type SampleBoardRecipeSourceItem =
    SampleBoardMaterialRecipeItem;
